import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useT } from "../i18n";
import HierarchyFilter from "../components/HierarchyFilter";
import SearchInput from "../components/SearchInput";
import { useAuthStore } from "../store/authStore";
import { useBreakpoint } from "../hooks/useBreakpoint";
import ProductDetailModal from "../components/ProductDetailModal";
import { IconOpen } from "../components/RowActionsMenu";
import ProductNameCell from "../components/ProductNameCell";
import MarketedBadge from "../components/MarketedBadge";
import { ESTRUCTURA_PDF } from "../lib/hitos";

type Hit = {
  cn_code: string; product_name: string; active_ingredient: string | null;
  laboratory: string | null; atc_code: string | null; is_comercializado: boolean;
  in_portfolio: boolean; in_competitors: boolean;
  importacion_paralela?: "ip" | "extranjero" | null; nregistro?: string | null;
};

const th: React.CSSProperties = { padding: "0.6rem 10px", textAlign: "left", fontSize: "0.65rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.9, color: "var(--clr-text4)", whiteSpace: "nowrap" };
// Anchos fijos: `tableLayout:"fixed"` permite truncar el nombre con «…».
// La columna Producto no lleva ancho — se queda el resto.
// Acciones: botones directos «+ Mis productos» / «+ Competidores» (feedback de Carlos, 20 oct 2026).
const COL_DESKTOP = { cn: 96, ingredient: 190, lab: 180, status: 150, actions: 200 } as const;
const COL_MOBILE  = { cn: 84, ingredient: 140, lab: 0,   status: 130, actions: 120 } as const;
const TABLE_MIN_DESKTOP = 1040;
const TABLE_MIN_MOBILE  = 620;
const td: React.CSSProperties = { padding: "0.6rem 10px" };

// Iconos del sidebar (Mis Productos = matraz, Competidores = escudo).
function IconBeaker() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" /></svg>;
}
function IconShield() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>;
}

function Chip({ label, color, icon }: { label: string; color: string; icon?: React.ReactNode }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: color + "1e", color, border: `1px solid ${color}33`, borderRadius: 999, padding: "2px 9px", fontSize: "0.66rem", fontWeight: 600, whiteSpace: "nowrap" }}>
      {icon ?? <span style={{ width: 6, height: 6, borderRadius: 3, background: color, flexShrink: 0 }} />}
      {label}
    </span>
  );
}

export default function CatalogPage() {
  const t = useT();
  const { isMobile } = useBreakpoint();
  const COL = isMobile ? COL_MOBILE : COL_DESKTOP;
  const tableMin = isMobile ? TABLE_MIN_MOBILE : TABLE_MIN_DESKTOP;
  const role = useAuthStore(s => s.user?.role);
  const canEdit = role !== "readonly";
  const [q, setQ] = useState("");
  const [hier, setHier] = useState("");          // R-35: atc|ah|spr|pa|nregistro:valor
  const [items, setItems] = useState<Hit[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [detailCn, setDetailCn] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [matching, setMatching] = useState(0);
  const [bulkBusy, setBulkBusy] = useState(false);

  const active = q.trim().length >= 2 || hier !== "";
  useEffect(() => {
    const term = q.trim();
    if (!active) { setItems([]); setSearched(false); setLoading(false); return; }
    setLoading(true);
    const h = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (term.length >= 2) params.set("q", term);
        if (hier) params.set("hier", hier);
        const r = await api.get<{ items: Hit[]; matching?: number }>(`/portfolio/catalog/search?${params}`);
        setItems(r.items); setMatching(r.matching ?? r.items.length); setSearched(true);
      } catch { /* ignore */ } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(h);
  }, [q, hier, active]);

  function showToast(msg: string, ok: boolean) { setToast({ msg, ok }); setTimeout(() => setToast(null), 3000); }

  async function addPortfolio(hit: Hit) {
    try {
      const r = await api.post<{ importacion_paralela?: boolean }>("/portfolio", { cn_code: hit.cn_code, product_name: hit.product_name, active_ingredient: hit.active_ingredient || "", laboratory: hit.laboratory });
      // Una importación paralela pasa a Competidores hasta que la org decida (feedback de Carlos, 20 oct).
      if (r?.importacion_paralela) {
        setItems(prev => prev.map(x => x.cn_code === hit.cn_code ? { ...x, in_competitors: true } : x));
        showToast(t("catalog.addedAsParallelImport"), true);
      } else {
        setItems(prev => prev.map(x => x.cn_code === hit.cn_code ? { ...x, in_portfolio: true } : x));
        showToast(t("catalog.addedPortfolio"), true);
      }
    } catch { showToast(t("catalog.addError"), false); }
  }
  async function addCompetitor(hit: Hit) {
    try {
      await api.post("/portfolio/competitors", { cn_code: hit.cn_code, product_name: hit.product_name, active_ingredient: hit.active_ingredient || "", company_name: hit.laboratory, competitor_type: "direct" });
      setItems(prev => prev.map(x => x.cn_code === hit.cn_code ? { ...x, in_competitors: true } : x));
      showToast(t("catalog.addedCompetitor"), true);
    } catch { showToast(t("catalog.addError"), false); }
  }

  // Alta en bloque: todos los productos comercializados del ATC o de la agrupación elegidos.
  async function bulkAdd(side: "portfolio" | "competitor") {
    if (!hier || bulkBusy) return;
    setBulkBusy(true);
    try {
      const r = await api.post<{ created: number; skipped: number; importaciones_paralelas: number }>(
        "/portfolio/catalog/bulk-add", { hier, side, q: q.trim() || null, only_marketed: true });
      showToast(t(side === "portfolio" ? "catalog.bulkAddedPortfolio" : "catalog.bulkAddedCompetitors",
                  { n: r.created, skipped: r.skipped }) + (r.importaciones_paralelas ? " " + t("catalog.bulkParallel", { n: r.importaciones_paralelas }) : ""), true);
      const params = new URLSearchParams();
      if (q.trim().length >= 2) params.set("q", q.trim());
      params.set("hier", hier);
      const res = await api.get<{ items: Hit[]; matching?: number }>(`/portfolio/catalog/search?${params}`);
      setItems(res.items);
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : t("catalog.addError"), false);
    } finally { setBulkBusy(false); }
  }
  const pill = (bg: string, fg: string): React.CSSProperties => ({ background: bg, color: fg, border: `1px solid ${fg}33`, borderRadius: 999,
    padding: "3px 10px", fontSize: "0.7rem", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" });

  return (
    <div style={{ padding: isMobile ? "1rem" : "1.75rem 2rem", color: "var(--clr-text)", fontVariantNumeric: "tabular-nums" }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em" }}>{t("catalog.pageTitle")}</h1>
        <p style={{ margin: "4px 0 0", fontSize: "0.82rem", color: "var(--clr-text4)" }}>
          {t("catalog.subtitle")} · <a href={ESTRUCTURA_PDF} target="_blank" rel="noreferrer" style={{ color: "var(--clr-org-primary)", fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap" }}>{t("hitos.structurePdf")} ↗</a>
        </p>
      </div>

      <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)", padding: "1rem 1.25rem" }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14, alignItems: "center" }}>
          <SearchInput autoFocus value={q} onChange={setQ} placeholder={t("catalog.searchPlaceholder")}
            minWidth={260} style={{ borderRadius: 999, padding: "8px 14px", maxWidth: 420 }} />
          {/* R-35: buscar por jerarquía (ATC, AH, SPR, PA, nregistro), con o sin texto */}
          <HierarchyFilter value={hier} onChange={setHier} />
        </div>

        {canEdit && hier && searched && matching > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", margin: "-4px 0 12px", padding: "8px 12px",
                        borderRadius: 12, background: "var(--clr-surface2, rgba(8,145,178,0.06))", border: "1px solid var(--clr-border2)" }}>
            <span style={{ fontSize: "0.8rem", color: "var(--clr-text2)" }}>{t("catalog.bulkIntro", { n: matching })}</span>
            <button disabled={bulkBusy} onClick={() => bulkAdd("portfolio")} style={pill("#0891b21e", "#0891b2")}>{t("catalog.bulkToPortfolio")}</button>
            <button disabled={bulkBusy} onClick={() => bulkAdd("competitor")} style={pill("#f59e0b1e", "#b45309")}>{t("catalog.bulkToCompetitors")}</button>
            <span style={{ fontSize: "0.72rem", color: "var(--clr-text4)" }}>{t("catalog.bulkNote")}</span>
          </div>
        )}

        {!active ? (
          <div style={{ padding: "2.5rem", textAlign: "center", color: "var(--clr-text4)", fontSize: "0.88rem" }}>{t("catalog.prompt")}</div>
        ) : loading ? (
          <div style={{ padding: "2.5rem", textAlign: "center", color: "var(--clr-text4)" }}>{t("catalog.loading")}</div>
        ) : searched && items.length === 0 ? (
          <div style={{ padding: "2.5rem", textAlign: "center", color: "var(--clr-text4)", fontSize: "0.88rem" }}>{t("catalog.noResults")}</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem", tableLayout: "fixed", minWidth: tableMin }}>
              <thead><tr style={{ borderBottom: "1px solid var(--clr-border2)" }}>
                {([["CN", COL.cn], [t("catalog.colProduct"), undefined], [t("catalog.colActiveIngredient"), COL.ingredient], ...(isMobile ? [] : [[t("catalog.colLaboratory"), COL.lab] as const]), [t("catalog.colStatus"), COL.status], ["", COL.actions]] as const)
                  .map(([h, w], i) => <th key={`${h}-${i}`} style={{ ...th, width: w }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {items.map(h => (
                  <tr key={h.cn_code} style={{ borderTop: "1px solid var(--clr-border2)" }}>
                    <td style={{ ...td, color: "var(--clr-text3)", fontSize: "0.78rem", whiteSpace: "nowrap" }}>{h.cn_code}</td>
                    <ProductNameCell
                      name={h.product_name}
                      title={h.product_name}
                      onClick={() => setDetailCn(h.cn_code)}
                      icon={<IconOpen />}
                    >
                      {(h.in_portfolio || h.in_competitors || h.importacion_paralela) && (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "0 8px 4px" }}>
                          {h.in_portfolio && <Chip label={t("catalog.inPortfolio")} color="#0891b2" icon={<IconBeaker />} />}
                          {h.in_competitors && <Chip label={t("catalog.inCompetitors")} color="#f59e0b" icon={<IconShield />} />}
                          {h.importacion_paralela && (
                            <span title={t(h.importacion_paralela === "ip" ? "catalog.parallelImportIp" : "catalog.parallelImportForeign", { nreg: h.nregistro || "" })}>
                              <Chip label={t("catalog.parallelImport")} color="#d97706" />
                            </span>
                          )}
                        </div>
                      )}
                    </ProductNameCell>
                    <td style={{ ...td, color: "var(--clr-text2)", textTransform: "uppercase", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={h.active_ingredient || undefined}>{h.active_ingredient || "–"}</td>
                    {!isMobile && <td style={{ ...td, color: "var(--clr-text3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={h.laboratory || undefined}>{h.laboratory || "–"}</td>}
                    <td style={td}>
                      <MarketedBadge marketed={h.is_comercializado} />
                    </td>
                    <td style={{ ...td }}>
                      {canEdit && (
                        <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
                          {!h.in_portfolio && !h.in_competitors && (
                            <button onClick={() => addPortfolio(h)} title={t("catalog.addToPortfolio")} style={pill("#0891b21e", "#0891b2")}>
                              {isMobile ? "+ " + t("catalog.short_portfolio") : t("catalog.btnPortfolio")}
                            </button>
                          )}
                          {!h.in_competitors && !h.in_portfolio && (
                            <button onClick={() => addCompetitor(h)} title={t("catalog.addToCompetitors")} style={pill("#f59e0b1e", "#b45309")}>
                              {isMobile ? "+ " + t("catalog.short_competitor") : t("catalog.btnCompetitors")}
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detailCn && <ProductDetailModal cn={detailCn} onClose={() => setDetailCn(null)} />}
      {toast && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.ok ? "#0891b2" : "#ef4444", color: "#fff", padding: "0.7rem 1.4rem", borderRadius: 12, fontSize: "0.85rem", fontWeight: 600, boxShadow: "0 8px 28px rgba(0,0,0,0.22)", zIndex: 9999 }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
